/* eslint-disable @typescript-eslint/no-var-requires, global-require */
// Firebase, imported from here by the rest of the game.
//
// Website, apps, Discord and the online portals: the real Firebase (sign-in, rooms, leaderboards).
// Its code is about half of the game's JavaScript, so it isn't part of the first download: the
// start screen shows without it, and it is fetched in the background as soon as the start screen
// is up (or straight away on any other page; see firebaseReady in index.tsx and the routes there).
// Code that may run before then waits for firebaseReady(); everything else (the game table, the
// create screen, sign-in...) only renders once it is ready, so it can use `firebase` as before.
//
// Playgama, GamePix and Y8 (isOfflinePortal in portal.ts): the game runs entirely in the browser there (local mode, outside connections are
// blocked), so that build gets an inert stand-in and ships without Firebase's code at all.
// Webpack drops the branch not taken. Unit tests load the real thing straight away.
let real: any = null; // the Firebase namespace, once loaded and initialised
let loading: Promise<any> | null = null;
const initHooks: Array<(fb: any) => void> = [];

const runInit = (fb: any) => {
  initHooks.splice(0).forEach((hook) => hook(fb));
  real = fb;
  return fb;
};

let syncFb: any = null;
if (process.env.REACT_APP_PORTAL === "playgama" || process.env.REACT_APP_PORTAL === "gamepix" || process.env.REACT_APP_PORTAL === "y8") {
  syncFb = require("./fbStub").default;
} else if (process.env.NODE_ENV === "test") {
  syncFb = require("./fbReal").default;
}

// Runs once, with the namespace, before anything else gets to use it (initializeApp and settings).
export const onFirebaseInit = (hook: (fb: any) => void) => {
  if (real) return hook(real);
  initHooks.push(hook);
  if (syncFb) runInit(syncFb); // stand-in / tests: there's nothing to wait for
};

// True once Firebase is loaded and initialised (always, in the Playgama build and in tests).
export const firebaseLoaded = () => real !== null || syncFb !== null;

// Loads (once) and initialises Firebase. Resolves with the namespace.
export const firebaseReady = (): Promise<any> => {
  if (real) return Promise.resolve(real);
  if (!loading) {
    // (A constant condition, so the Playgama build doesn't even contain the chunk.)
    loading =
      (process.env.REACT_APP_PORTAL === "playgama" || process.env.REACT_APP_PORTAL === "gamepix" || process.env.REACT_APP_PORTAL === "y8")
        ? Promise.resolve(syncFb).then(runInit)
        : syncFb
        ? Promise.resolve(syncFb).then(runInit)
        : import(/* webpackChunkName: "firebase" */ "./fbReal").then((m) => runInit(m.default));
    // A failed download (offline, flaky network) can be retried by the next caller.
    loading.catch(() => {
      loading = null;
    });
  }
  return loading;
};

// The stand-in and the unit tests have it at once: initialise on first use, as before.
const ensureSync = () => {
  if (!real && syncFb) runInit(syncFb);
  return real;
};

// `firebase` itself: the loaded namespace. Using it before firebaseReady() has resolved is a bug
// (the page renders before Firebase arrives), so it says so instead of failing somewhere obscure.
const fb: any = new Proxy(function () {}, {
  get(_t, prop) {
    const ns = real || ensureSync();
    if (!ns) throw new Error(`Firebase used before it loaded (firebase.${String(prop)}); await firebaseReady() first`);
    return ns[prop];
  },
  has(_t, prop) {
    const ns = real || ensureSync();
    return Boolean(ns) && prop in ns;
  },
});
export default fb;
